import React, { useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Media from 'react-media';
import Form from 'semantic-ui-react/dist/commonjs/collections/Form';
import UIButton from '../ui/button';
import UIMessage from '../ui/message';
import GoogleRecaptchaBranding from '../legal/recaptcha_branding';
import SubscribeSuccessBox from '../subscribe_success_box/subscribe_success_box';
import SubscribeGenderInterestRadioButtons from './gender_interest_radio_buttons';
import useSubscribeBox from './useSubscribeBox';
import { scrollIntoView } from '../../utils/scroll_into_view';
import { useTranslation } from '../common/localized';
import I18nContext from '../../contexts/i18n';
import FlexWrapper from '../common/flex';

const SubscribeBox = ({
    noName, forPopup, promotion, invertedColors, onSubscribe, smaller, customAction, removeTitle, content
  }) => {
  const errorMessageElement = useRef(null);
  const {
    name,
    email,
    subscribeGenderInterest,
    waiting,
    finished,
    errorMessage,
    errorCounter,
    onChangeName,
    onChangeEmail,
    onChangeSubscribeGenderInterest,
    validateAndSubmit
  } = useSubscribeBox(noName, promotion, onSubscribe);
  const { t } = useTranslation();
  const i18nSettings = useContext(I18nContext);
  const isRtl = i18nSettings.isLocaleRTL();

  useEffect(() => {
    if (errorMessage && errorMessageElement.current) scrollIntoView(errorMessageElement.current);
  }, [errorCounter, errorMessage, errorMessageElement]);

  if (finished) return <SubscribeSuccessBox invertedColors={invertedColors} />;

  const InputComp = isRtl ? RTLInput : LTRInput;
  return (
    <Media query='(min-width: 360px)'>
      {matches =>
        <SubscribeBoxWrapper>
          <Form {...{ error: !!errorMessage }} {...{ loading: waiting }}
            size={matches && !smaller ? 'large' : 'small'} onSubmit={validateAndSubmit} unstackable>
            <UIMessage ref={errorMessageElement} error compact={smaller} size='tiny' content={errorMessage} />
            {!forPopup
              ? <>
                  {smaller &&
                    <SubscribeGenderInterestRadioButtons value={subscribeGenderInterest} removeTitle={removeTitle}
                      onChange={onChangeSubscribeGenderInterest} invertedColors={invertedColors} />}
                  {!noName && <Form.Input name='name' fluid placeholder='Name' value={name} onChange={onChangeName} />}
                  {!customAction &&
                    <InputComp name='email' fluid placeholder={t('Email address')} aria-label='email' value={email} dir='ltr'
                      actionPosition={isRtl ? 'left' : 'right'} onChange={onChangeEmail}
                      action={smaller
                        ? { color: 'black', icon: isRtl ? 'arrow left' : 'arrow right' }
                        : undefined} />}
                  {customAction &&
                    <Form.Input name='email' fluid placeholder={t('Email address')}
                      aria-label='email' value={email} onChange={onChangeEmail} action={customAction} />}
                  {!smaller &&
                    <SubscribeGenderInterestRadioButtons value={subscribeGenderInterest}
                      onChange={onChangeSubscribeGenderInterest} invertedColors={invertedColors} />}
                  {!smaller && <UIButton submit taller fluid content='Subscribe' noMargin />}
                  <GoogleRecaptchaBranding squeezeToTop={smaller} white={invertedColors} />
                </>
              : <SubscribeWithPopup t={t} email={email} onChangeEmail={onChangeEmail} removeTitle={removeTitle}
                  subscribeGenderInterest={subscribeGenderInterest} invertedColors={invertedColors}
                  onChangeSubscribeGenderInterest={onChangeSubscribeGenderInterest} content={content} />}
          </Form>
        </SubscribeBoxWrapper>}
    </Media>
  );
};

if (process.env.NODE_ENV !== 'production') {
  SubscribeBox.propTypes = {
    noName: PropTypes.bool,
    promotion: PropTypes.string,
    smaller: PropTypes.bool,
    onSubscribe: PropTypes.func,
    invertedColors: PropTypes.bool
  };
}

SubscribeBox.defaultProps = {
  noName: false,
  smaller: false,
  invertedColors: false
};

const SubscribeBoxWrapper = styled.div`
  max-width: 280px;
  @media screen and (min-width: 360px) { max-width: 320px; }
  @media screen and (min-width: 375px) { max-width: 340px; }
  @media screen and (min-width: 411px) { max-width: 360px; }
  @media screen and (min-width: 600px) { max-width: 420px; }
  @media screen and (min-width: 992px) { max-width: 480px; }
  width: 100%;
`;

const LTRInput = styled(Form.Input)`
  .ui.input input, .ui.input input::placeholder {
    text-align: left;
  }
`;

const RTLInput = styled(Form.Input)`
  .ui.input input, .ui.input input::placeholder {
    text-align: right;
  }
`;

const SubscribeWithPopup = (props) => {
  const {
    t, email, onChangeEmail, subscribeGenderInterest, onChangeSubscribeGenderInterest, removeTitle, content,
    invertedColors
  } = props;
  return (
    <SubscribeContainer vertical>
      <Input name='email' fluid placeholder={t('Enter your email')} value={email} onChange={onChangeEmail} />
      <SubscribeGenderInterestRadioButtons forPopup value={subscribeGenderInterest} removeTitle={removeTitle}
        onChange={onChangeSubscribeGenderInterest} invertedColors={invertedColors} />
      <UIButton submit color='solid' boxy fontWeight='100' content={t(content)} />
    </SubscribeContainer>
  );
};

const SubscribeContainer = styled(FlexWrapper)`
  .lf-nice-button.solid {
    margin-top: 60px;
    font-size: 18px;
    padding: 12px 22px 12px 22px;

    @media screen and (max-width: 768px) {
      margin-top: 14px;
      font-size: 12px;
      padding: 8px;
      max-height: 50%;
    }
  }
`;

const Input = styled.input`
  border: 1px solid black !important;
  border-radius: 0 !important;

  @media screen and (max-width: 768px) {
    width: 60% !important;
    font-size: 12px !important;
  }
`;

export default SubscribeBox;
